/* Trek Light accessibility patches for third-party app markup.
   Upgrade-safe: this file is ours, nothing in an app is edited.
   Everything here is idempotent and must never change app behaviour.

   1. Offerly PWYC modal ships two <label for> attributes (offerName, offerEmail)
      that point at ids which do not exist; both inputs carry the same id
      ("OfferInput"), so neither input has an accessible name. We point each
      input at its sibling label with aria-labelledby. We do NOT rewrite the
      ids: Offerly's own script reads them.
   2. The modal also ships two <h2> elements that are empty until a product is
      selected (#mobileModalProductTitle, #mobileModalPrice). An empty heading
      is a WAVE error, so we mark them presentational while empty and restore
      them the moment they get content. We also stamp data-tl-empty on them,
      which assets/morning-light-global.css turns into display:none: WAVE
      counts an empty heading whatever its role, and only a heading that is
      not rendered at all stops being counted.
   3. The facets price range slider ships two role="slider" thumbs with no
      accessible name. They come from the minified upstream rangeSlider bundle
      in component-facets.js, so we name them here. The store is English only,
      so the strings live in this file rather than in new Liquid data
      attributes on a native snippet.
   4. The nav drawer and the filters drawer are role="dialog" with no
      accessible name. Each ships a visible .sidebar__title, so we point the
      dialog at that element with aria-labelledby rather than copying the
      string: the titles are localized through the theme's locale files and a
      copy would go stale.
   5. Judge.me's expanded media grid puts role="listitem" on <button>
      elements, which is not an allowed role for that element. The role and
      its parent role="list" are decoration Judge.me added on top of markup
      that is already fine without them, so both come off. Scoped to
      Judge.me's own grid class and nothing else of theirs is touched.
   6. Adoric's bundle widget renders an always-empty <h6> (an empty heading
      that also skips a heading level) and wraps each price in a bare <nav>,
      which makes several identically unnamed navigation landmarks per page.
      A price is not navigation, so the wrapper is marked presentational,
      which removes the false landmark rather than naming it. Same technique
      as 2 for the empty heading. Adoric renders into an OPEN shadow root,
      so neither document.querySelectorAll nor the page observer below
      reaches it: this one patches through the shadow root and attaches its
      own observer to each. axe crosses open shadow boundaries, which is why
      its targets for these came back as a comma-joined selector pair.
   7. Everything from THEME_PATCHES down was previously applied by editing
      native Combine files. Those edits are merge conflicts at upgrade time,
      so each one was reproduced here instead and the native file put back
      byte for byte. Same fixes, no upgrade debt. The one residual limitation:
      a scanner that reads raw HTML without rendering it will not see an
      attribute applied by script. This file is deferred, so it runs after
      parsing and before DOMContentLoaded, which means axe, WAVE, Lighthouse
      and real assistive tech all see the finished DOM.
   8. Blog articles carry merchant-pasted media embeds (Spotify, Apple Music,
      YouTube, Bandcamp, GIPHY) that ship with no title attribute, so every
      one of them fails axe frame-title. The embeds live in article rich text
      AND in the Weekly Light custom-liquid and metafield sections, so the
      scope is the main landmark on an article template rather than
      .article__content alone, which would miss every Weekly Light embed.
      App-owned iframes are skipped: their findings belong to the app report,
      not to us. The title is derived from the src host so it stays correct
      for articles that do not exist yet, and repeats within one page get a
      numeric suffix so naming them cannot introduce frame-title-unique.
   9. layout/theme.liquid wraps every rich-text table in .table-wrapper, which
      is overflow-x:auto. A region that scrolls must be reachable by keyboard
      (axe scrollable-region-focusable, WCAG 2.1.1), and on mobile the widest
      article table does scroll. Focusability is applied only while the
      wrapper actually overflows, so a table that fits never becomes a stray
      tab stop, and it is withdrawn again if a resize makes it fit.
*/
(function () {
  'use strict';

  var labelSeq = 0;

  /* Point the input at its label with aria-labelledby rather than copying
     the text into aria-label. tlSetMode() in product-custom.js rewrites the
     amount field's visible label when the shopper switches to Pay It
     Forward, and a copied string would keep announcing the old name. */
  function nameOfferlyInputs(root) {
    var inputs = root.querySelectorAll('.offerInput');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      /* An earlier revision of this file wrote aria-label. Clear it so a
         stale copied string cannot outrank the live label. */
      if (input.hasAttribute('aria-label')) input.removeAttribute('aria-label');

      var label = input.previousElementSibling;
      while (label && label.tagName !== 'LABEL') label = label.previousElementSibling;
      /* Only fall back to an ancestor's label when that ancestor holds a
         single field. A wrong accessible name is worse than none. */
      if (!label) {
        var box = input.closest('div');
        if (box && box.querySelectorAll('.offerInput').length === 1) label = box.querySelector('label');
      }
      if (!label) continue;

      if (!label.id) label.id = 'tl-offerly-label-' + (++labelSeq);
      if (input.getAttribute('aria-labelledby') !== label.id) {
        input.setAttribute('aria-labelledby', label.id);
      }
    }
  }

  /* Both the mobile and the desktop copy of the modal ship these headings, so
     this sweeps every heading Offerly renders rather than naming ids. */
  function syncEmptyHeadings(root) {
    var headings = root.querySelectorAll(
      '#mobileModalProductTitle, #mobileModalPrice, #modalProductTitle, #modalPrice,' +
      ' [class*="offerly"] h1, [class*="offerly"] h2, [class*="offerly"] h3,' +
      ' [class*="offerly"] h4, [class*="offerly"] h5, [class*="offerly"] h6'
    );
    for (var i = 0; i < headings.length; i++) {
      var el = headings[i];
      if ((el.textContent || '').trim() === '') {
        if (el.getAttribute('role') !== 'presentation') el.setAttribute('role', 'presentation');
        /* Driven off the real trimmed text, so this can never hide a heading
           that has content. The CSS fallback that covers the window before
           this script runs is keyed on html:not(.tl-a11y-ready). */
        if (el.getAttribute('data-tl-empty') !== 'true') el.setAttribute('data-tl-empty', 'true');
      } else {
        if (el.getAttribute('role') === 'presentation') el.removeAttribute('role');
        if (el.hasAttribute('data-tl-empty')) el.removeAttribute('data-tl-empty');
      }
    }
  }

  /* The thumbs are created by the slider library after the snippet renders, so
     this runs on every pass and is guarded to be a no-op once they are named.
     The filter is always the price filter, and the store is English only, so
     the two names are constants here instead of data attributes added to
     snippets/filter-price-range.liquid. This also names the thumbs in the
     mobile filter drawer, which renders a second copy of the same slider, and
     it re-applies after a facet re-render replaces them. */
  var PRICE_THUMB_LOWER = 'Price From';
  var PRICE_THUMB_UPPER = 'Price To';

  function nameRangeSliderThumbs(root) {
    var sliders = root.querySelectorAll('[data-js-price-range-slider]');
    for (var i = 0; i < sliders.length; i++) {
      var slider = sliders[i];
      var pairs = [
        ['.range-slider__thumb[data-lower]', PRICE_THUMB_LOWER],
        ['.range-slider__thumb[data-upper]', PRICE_THUMB_UPPER]
      ];
      for (var j = 0; j < pairs.length; j++) {
        var thumb = slider.querySelector(pairs[j][0]);
        if (thumb && thumb.getAttribute('aria-label') !== pairs[j][1]) {
          thumb.setAttribute('aria-label', pairs[j][1]);
        }
      }
    }
  }

  /* Postscript aria-hides page content while its popup runs and restores the
     original value afterwards, tracked in data-original-aria-hidden. When the
     original was null it leaves aria-hidden="true" behind, and the Offerly
     modal inherits it: the dialog is on screen and interactive while being
     invisible to a screen reader, which also nullifies the labels above.
     Only unhide it while it is actually displayed. */
  function unhideOpenOfferlyModal(root) {
    var modal = root.querySelector('.OfferlyModal');
    if (!modal) return;
    if (modal.getAttribute('aria-hidden') !== 'true') return;

    /* While Postscript's popup is actually open, the aria-hidden it applies is
       correct: the popup covers the page and the content beneath it should stay
       out of the accessibility tree. Only the leftover counts as stale, so do
       nothing until the popup has closed. */
    if (root.querySelector('#ps__widget_container[data-ps-popup-status="open"]')) return;

    /* Only touch the attribute Postscript itself left behind. The marker holds
       the value to restore, and the string "null" means there was none, so the
       attribute should have been removed rather than left at "true". Anything
       else is somebody's deliberate aria-hidden and is not ours to clear. */
    if (modal.getAttribute('data-original-aria-hidden') !== 'null') return;

    /* A display:none box measures zero, so the size checks this used to
       make first never changed the answer; they only forced a layout. */
    var displayed = window.getComputedStyle(modal).display !== 'none';
    if (displayed) modal.removeAttribute('aria-hidden');
  }

  /* sections/header.liquid and sections/main-collection-product-grid.liquid /
     sections/main-search.liquid render <sidebar-drawer role="dialog"> with no
     accessible name (axe aria-dialog-name). Both already show a visible
     .sidebar__title, which is the name a sighted shopper reads, so point the
     dialog at it. aria-labelledby rather than aria-label keeps the name tied
     to the theme's own localized string instead of a copy that would go stale.
     Guarded on both naming attributes so a drawer that is already named (or
     one a future section names in Liquid) is left alone. */
  var drawerSeq = 0;

  function nameSidebarDrawers(root) {
    var drawers = root.querySelectorAll(
      'sidebar-drawer[role="dialog"]:not([aria-label]):not([aria-labelledby])'
    );
    for (var i = 0; i < drawers.length; i++) {
      var drawer = drawers[i];
      var title = drawer.querySelector('.sidebar__header .sidebar__title, .sidebar__title');
      var text = title && (title.textContent || '').trim();
      if (!text) continue;
      if (!title.id) title.id = 'tl-drawer-title-' + (++drawerSeq);
      drawer.setAttribute('aria-labelledby', title.id);
    }
  }

  /* Judge.me's expanded media grid: every thumbnail is a <button> carrying
     role="listitem", which is not an allowed role for that element (axe
     aria-allowed-role), and the grid around them carries role="list". Both
     are decoration on markup that reads perfectly well without them, and
     dropping only the child role would leave the list with children that are
     not list items, so the pair comes off together. Scoped to Judge.me's own
     class names: nothing else of theirs is touched, and the buttons keep
     their aria-label, their handlers and their appearance. */
  function dropJudgemeGridRoles(root) {
    var items = root.querySelectorAll('button.jm-expanded-media-grid__item[role]');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.getAttribute('role') !== 'listitem') continue;
      var list = item.parentElement;
      if (list && list.getAttribute('role') === 'list' &&
          list.querySelector('button.jm-expanded-media-grid__item')) {
        list.removeAttribute('role');
      }
      item.removeAttribute('role');
    }
  }

  /* Adoric's bundle widget (<adoric-bundle-mark-two>) ships two defects on
     every bundle PDP:
       - an <h6> that is always empty, which is both an empty heading and a
         skipped heading level under the section's h2;
       - each price wrapped in a bare <nav>, so a page with several bundle
         products carries several identically unnamed navigation landmarks
         (axe landmark-unique).
     The heading is marked presentational while it is empty, exactly as the
     Offerly headings above are, and restored the moment Adoric fills it. The
     price wrapper is marked presentational too rather than given a name: a
     price is not navigation, and naming it would leave a real landmark in the
     landmarks list that no shopper wants to jump to. Neither write changes
     anything on screen or any behaviour of the widget. */
  var adoricObserved = [];

  function patchAdoricRoot(scope) {
    var headings = scope.querySelectorAll('h1, h2, h3, h4, h5, h6');
    for (var h = 0; h < headings.length; h++) {
      var el = headings[h];
      if ((el.textContent || '').trim() === '') {
        if (el.getAttribute('role') !== 'presentation') el.setAttribute('role', 'presentation');
      } else if (el.getAttribute('role') === 'presentation') {
        el.removeAttribute('role');
      }
    }

    /* Only a <nav> with no link in it. A navigation landmark that really does
       hold links is left exactly as Adoric shipped it. */
    var navs = scope.querySelectorAll('nav:not([role])');
    for (var n = 0; n < navs.length; n++) {
      if (navs[n].querySelector('a, [role="link"]')) continue;
      navs[n].setAttribute('role', 'presentation');
    }
  }

  /* Both bundle types, matching the HOSTS list in assets/ml-bundle-adoric.js.
     Only mark-two was on a crawled page, but the two widgets share their
     markup, so mark-seven carries the same defects wherever it is used. */
  var ADORIC_HOSTS = ['adoric-bundle-mark-two', 'adoric-bundle-mark-seven'];

  function patchAdoricBundle(root) {
    var widgets = root.querySelectorAll(ADORIC_HOSTS.join(','));
    for (var w = 0; w < widgets.length; w++) {
      /* Adoric renders into an open shadow root, which the page-level
         observer below cannot see and document.querySelectorAll cannot
         reach. Patch inside it and give each one its own observer, once.
         The element is upgraded before its shadow root is populated, so the
         root can be absent on an early pass; the page observer brings us
         back. */
      var scope = widgets[w].shadowRoot;
      if (!scope) continue;
      patchAdoricRoot(scope);

      if (adoricObserved.indexOf(scope) !== -1) continue;
      adoricObserved.push(scope);
      if (!('MutationObserver' in window)) continue;
      (function (shadow) {
        var pending = false;
        new MutationObserver(function () {
          if (pending) return;
          pending = true;
          window.requestAnimationFrame(function () {
            pending = false;
            try { patchAdoricRoot(shadow); } catch (e) { /* never break the widget */ }
          });
        }).observe(shadow, { childList: true, subtree: true, characterData: true });
      })(scope);
    }
  }

  /* ------------------------------------------------------------------ *
   * THEME_PATCHES
   * Fixes that used to live in native Combine files. Each one names the
   * file it came out of so the pairing stays obvious at upgrade time.
   * ------------------------------------------------------------------ */

  var SLIDE_LABEL = 'Show slide ';          /* general.accessibility_labels.slide */
  var MENU_LABEL = 'Menu';                  /* general.accessibility_labels.menu */
  var DISCOUNT_LABEL = 'Discount Code';     /* cart.discount_codes_label */

  function shopName() {
    var meta = document.querySelector('meta[property="og:site_name"]');
    var name = meta && (meta.getAttribute('content') || '').trim();
    return name || 'Main';
  }

  /* sections/main-product.liquid, sections/featured-product.liquid and
     sections/product-quick-view.liquid: the thumbnail strip carried
     aria-hidden="true" while holding tabbable buttons, which is both a
     focusable-inside-aria-hidden failure and a gallery no screen reader
     could use. The buttons themselves had no accessible name. */
  function patchGalleryThumbnails(root) {
    var strips = root.querySelectorAll('.product-gallery__thumbnails[aria-hidden="true"]');
    for (var i = 0; i < strips.length; i++) strips[i].removeAttribute('aria-hidden');

    var thumbs = root.querySelectorAll('.product-gallery__thumbnails button.thumbnail[data-index]');
    for (var t = 0; t < thumbs.length; t++) {
      var btn = thumbs[t];
      if (btn.getAttribute('aria-label')) continue;
      var n = parseInt(btn.getAttribute('data-index'), 10);
      if (isNaN(n)) continue;
      btn.setAttribute('aria-label', SLIDE_LABEL + (n + 1));
    }
  }

  /* snippets/facets-horizontal.liquid and snippets/facets-vertical.liquid:
     the sort select pointed aria-describedby at #a11y-refresh-page-message,
     an id this theme never renders. A dangling reference is an axe violation
     and gives the control no description either way. */
  function dropDanglingSortDescribedby(root) {
    if (document.getElementById('a11y-refresh-page-message')) return;
    var selects = root.querySelectorAll('select[aria-describedby="a11y-refresh-page-message"]');
    for (var i = 0; i < selects.length; i++) selects[i].removeAttribute('aria-describedby');
  }

  /* snippets/cart-subtotal.liquid: the cart page discount field duplicated
     the cart drawer's ids (CartPage-Discount, cartDiscountSubmit) and had no
     label, so it had no accessible name and the page carried two duplicate
     ids. The drawer copy is the one with a real label element, so it keeps
     the original ids and only the cart page copy is renamed. Nothing reads
     these ids: the theme's cart script binds on the data-js-* attributes. */
  function nameCartDiscountInput(root) {
    var buttons = root.querySelectorAll('button.cart__discount-apply[data-js-discount-submit]');
    for (var i = 0; i < buttons.length; i++) {
      var form = buttons[i].closest('.cart__coupons-form');
      if (!form) continue;
      var input = form.querySelector('input[data-js-discount-input]');
      if (!input) continue;
      if (!input.getAttribute('aria-label')) input.setAttribute('aria-label', DISCOUNT_LABEL);
      if (input.id === 'CartPage-Discount') input.id = 'CartPageMain-Discount';
      if (buttons[i].id === 'cartDiscountSubmit') buttons[i].id = 'cartPageMainDiscountSubmit';
    }
  }

  /* sections/main-article-navigation.liquid: an h3 directly after the article
     body's h2s, so the heading order skipped a level. The element is not
     replaced, which would mean editing the section. role and aria-level
     change what assistive tech computes and leave the markup alone. */
  function fixArticleNavHeadingLevel(root) {
    /* Section ids are prefixed with the template key on JSON templates, so
       match the suffix rather than the bare section name. */
    var h = root.querySelector('[id$="main-article-navigation"] > h3');
    if (!h) return;
    if (h.getAttribute('role') !== 'heading') h.setAttribute('role', 'heading');
    if (h.getAttribute('aria-level') !== '2') h.setAttribute('aria-level', '2');
  }

  /* sections/header.liquid: the desktop menu and the mobile sidebar menu were
     both unnamed nav landmarks, which axe landmark-unique cannot tell
     apart. */
  function nameHeaderNavLandmarks(root) {
    var desktop = root.querySelectorAll('.site-header .site-nav-container > nav:not([aria-label])');
    for (var i = 0; i < desktop.length; i++) desktop[i].setAttribute('aria-label', shopName());

    var mobile = root.querySelectorAll('.site-nav.style--sidebar .site-nav-container > nav:not([aria-label])');
    for (var m = 0; m < mobile.length; m++) mobile[m].setAttribute('aria-label', MENU_LABEL);
  }

  /* sections/footer.liquid: every footer menu block renders a bare nav, so a
     footer with three menus had three identically unnamed landmarks. The
     block's own heading sits beside the nav inside .footer-item. */
  function nameFooterMenuLandmarks(root) {
    var navs = root.querySelectorAll('nav.footer-item__menu:not([aria-label])');
    for (var i = 0; i < navs.length; i++) {
      var item = navs[i].closest('.footer-item');
      var title = item && item.querySelector('.footer-item__title');
      var text = title && (title.textContent || '').trim();
      if (text) navs[i].setAttribute('aria-label', text);
    }
  }

  /* assets/section-header.js: the sticky header is a clone the script appends
     straight to body, so its logo and cart link were content in no landmark
     and its nav was a second landmark with the same name as the real one. The
     clone is position:fixed, so where it sits in the DOM is free. Moving it
     from here is safe: an observer callback runs after the script that
     created it has finished its own id and aria-controls rewrites. */
  function adoptStickyHeaderClone(root) {
    var landmark = document.querySelector('header.site-header-landmark');
    if (!landmark) return;
    var clones = root.querySelectorAll('.sticky-header');
    for (var i = 0; i < clones.length; i++) {
      var clone = clones[i];
      if (!landmark.contains(clone)) landmark.appendChild(clone);
      var navs = clone.querySelectorAll('nav[aria-label]');
      for (var n = 0; n < navs.length; n++) {
        var label = navs[n].getAttribute('aria-label');
        if (label.indexOf(' sticky header') === -1) {
          navs[n].setAttribute('aria-label', label + ' sticky header');
        }
      }
    }
  }

  /* assets/component-slider.js: the slider viewport scrolls horizontally but
     its holder was never focusable, so a keyboard shopper could not reach the
     off-screen slides at all. The class carries the theme's prefix, hence the
     substring match. */
  function focusableSliderHolders(root) {
    var holders = root.querySelectorAll('[class*="css-slider-holder"]');
    for (var i = 0; i < holders.length; i++) {
      if (holders[i].getAttribute('tabindex') !== '0') holders[i].setAttribute('tabindex', '0');
    }
  }

  /* snippets/video-component.liquid: autoplaying videos with no control
     surface are silent decorative loops in this theme, so they carry no
     caption duty and hiding them is what takes axe video-caption out of
     scope. A video that has controls (rich text and shoppable video can pass
     force_controls) is NOT hidden: hiding a control surface from assistive
     tech would be a regression, not a fix. Click to play videos keep their
     caption duty too. Same condition the Liquid used. */
  function hideDecorativeVideos(root) {
    var videos = root.querySelectorAll('video[autoplay]:not([controls]):not([aria-hidden])');
    for (var i = 0; i < videos.length; i++) videos[i].setAttribute('aria-hidden', 'true');
  }

  /* sections/main-product.liquid: component-product-form.js relocates the
     sticky add-to-cart bar to the end of body, outside every landmark. */
  function nameStickyAddToCart(root) {
    var bars = root.querySelectorAll('.sticky-add-to-cart[data-id]');
    if (!bars.length) return;
    var h1 = document.querySelector('main h1');
    var title = h1 ? (h1.textContent || '').trim() : '';
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].getAttribute('role') !== 'region') bars[i].setAttribute('role', 'region');
      if (title && bars[i].getAttribute('aria-label') !== title) {
        bars[i].setAttribute('aria-label', title);
      }
    }
  }

  /* sections/main-product.liquid, sections/featured-product.liquid,
     snippets/quick-buy.liquid and assets/component-product-form.js: the
     "disabled" class dims the add to cart button to 54% opacity and blocks
     pointer events, but said nothing to assistive tech. aria-disabled
     announces the state and puts the control inside WCAG's inactive
     component exemption. Driven off the live class, so the variant switcher
     keeps it in sync for free: the observer already watches class. */
  function syncAriaDisabledButtons(root) {
    var buttons = root.querySelectorAll('[data-js-product-add-to-cart]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (btn.classList.contains('disabled')) {
        if (btn.getAttribute('aria-disabled') !== 'true') btn.setAttribute('aria-disabled', 'true');
      } else if (btn.hasAttribute('aria-disabled')) {
        btn.removeAttribute('aria-disabled');
      }
    }
  }

  /* assets/component-product-image-zoom.js. The #zoom overlay is built and
     appended to body at runtime by that script, so everything it needed is
     done here instead: the close control becomes a real named button that
     answers Enter, Space and Escape, its icon is marked decorative, focus
     returns to whatever opened the overlay, and the magnified image inherits
     the alt text of the gallery image it is showing. */
  var zoomOpener = null;

  function zoomSourceAlt(trigger) {
    var scope = trigger.closest('.product-gallery-item, .product-gallery__item, li') || trigger.parentElement;
    if (!scope) return '';
    var wanted = (trigger.getAttribute('data-image') || '').split('?')[0].split('/').pop();
    var images = scope.querySelectorAll('img');
    for (var i = 0; i < images.length; i++) {
      var src = (images[i].getAttribute('src') || '').split('?')[0].split('/').pop();
      if (wanted && src === wanted) return images[i].getAttribute('alt') || '';
    }
    /* No filename match (a responsive srcset can serve a differently named
       file): the gallery item holds one picture, so fall back to it. A wrong
       alt is worse than none, so anything else falls back to empty. */
    return (images.length === 1 && images[0].getAttribute('alt')) || '';
  }

  function restoreZoomFocus() {
    var zoom = document.getElementById('zoom');
    var img = zoom && zoom.querySelector('img');
    if (img) img.setAttribute('alt', '');
    var opener = zoomOpener;
    zoomOpener = null;
    if (opener && opener.isConnected) {
      if (!opener.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(opener.tagName)) {
        opener.setAttribute('tabindex', '-1');
      }
      opener.focus({ preventScroll: true });
    }
  }

  /* Let the theme's own handler run the close, so the animation, the listener
     teardown and the src reset all stay in one place. */
  function closeZoom() {
    var zoom = document.getElementById('zoom');
    if (!zoom) return;
    var exit = zoom.querySelector('.zoom__exit');
    if (exit) exit.click();
  }

  /* The #zoom overlay and the Offerly PWYC modal are both appended straight
     to body and cover the whole page while open, but neither says it is a
     dialog, so every control in them is content outside any landmark (axe
     region, flagged by the live patrol on 2026-10-03). Saying what they are
     fixes that truthfully: axe treats a named dialog as a region. Both are
     hidden from the accessibility tree while closed (#zoom by visibility,
     Offerly by display:none), so the role is only ever exposed while open.
     No aria-modal: neither moves or traps focus, and claiming modality
     without that would tell a screen reader the page behind is unreachable
     while Tab still walks it (Codex P2, 2026-10-04). Guarded writes, so a re-run never wakes the observer. */
  function setAttrIfChanged(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function markOverlayDialogs(root) {
    var zoom = root.querySelector('#zoom');
    if (zoom) {
      setAttrIfChanged(zoom, 'role', 'dialog');
      if (!zoom.getAttribute('aria-label')) zoom.setAttribute('aria-label', 'Zoomed product image');
    }
    var offerly = root.querySelector('#OfferlyModalDiv');
    if (offerly) {
      setAttrIfChanged(offerly, 'role', 'dialog');
      if (!offerly.getAttribute('aria-labelledby') && !offerly.getAttribute('aria-label')) {
        if (root.querySelector('#offerlyModalHeader')) offerly.setAttribute('aria-labelledby', 'offerlyModalHeader');
        else offerly.setAttribute('aria-label', 'Pay what you can');
      }
    }
  }

  function patchZoomOverlay(root) {
    var zoom = root.querySelector('#zoom');
    if (!zoom) return;
    var exit = zoom.querySelector('.zoom__exit');
    if (!exit) return;

    if (exit.getAttribute('role') !== 'button') exit.setAttribute('role', 'button');
    if (exit.getAttribute('tabindex') !== '0') exit.setAttribute('tabindex', '0');
    if (!exit.getAttribute('aria-label')) {
      exit.setAttribute('aria-label',
        (window.KROWN && KROWN.settings && KROWN.settings.locales && KROWN.settings.locales.close) || 'Close');
    }
    /* WAVE evaluates the svg itself and reports it as an image with no
       alternative text. Mark the graphic decorative on the element that
       carries it and keep it out of the tab order. The accessible name still
       comes from the aria-label above. */
    /* Guarded: the observer watches aria-hidden, so an unconditional write here
       would mutate, wake the observer, run this pass again and never settle
       (Codex P1, 2026-09-21). Every write in this file has to be a no-op once
       the value is already right. */
    var svgs = exit.querySelectorAll('svg');
    for (var i = 0; i < svgs.length; i++) {
      var svg = svgs[i];
      if (svg.getAttribute('aria-hidden') !== 'true') svg.setAttribute('aria-hidden', 'true');
      if (svg.getAttribute('focusable') !== 'false') svg.setAttribute('focusable', 'false');
      if (svg.hasAttribute('role')) svg.removeAttribute('role');
    }

    if (exit.getAttribute('data-tl-zoom-bound') === 'true') return;
    exit.setAttribute('data-tl-zoom-bound', 'true');
    exit.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        closeZoom();
      }
    });
    /* Runs after the theme's own click handler has closed the overlay, so
       this only has to put focus back and clear the alt. */
    exit.addEventListener('click', restoreZoomFocus);
  }

  function bindZoomTriggers() {
    /* Capture phase: record where focus was before the theme's handler opens
       the overlay. A keyboard shopper who closes it would otherwise be left
       on a hidden element at the top of the page. */
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('product-image-zoom[data-image]');
      if (!trigger) return;
      zoomOpener = (document.activeElement && document.activeElement !== document.body)
        ? document.activeElement : trigger;
    }, true);

    /* Bubble phase: by now the theme's handler has set the overlay image src,
       so the magnified copy can be given the gallery image's own alt text.
       Without it the zoomed view is an unlabelled image on every product
       page. */
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('product-image-zoom[data-image]');
      if (!trigger) return;
      patchZoomOverlay(document);
      var zoom = document.getElementById('zoom');
      var img = zoom && zoom.querySelector('img');
      if (img) img.setAttribute('alt', zoomSourceAlt(trigger));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var zoom = document.getElementById('zoom');
      if (zoom && zoom.classList.contains('opened')) closeZoom();
    });
  }

  /* Merchant-pasted media embeds in blog articles ship with no title, so a
     screen reader announces a bare "frame". These are the hosts measured
     across the 128 article crawl; anything else still gets a truthful
     generic name rather than nothing. */
  var EMBED_HOST_NAMES = [
    { test: /(^|\.)spotify\.com$/i, name: 'Spotify player' },
    { test: /(^|\.)music\.apple\.com$/i, name: 'Apple Music player' },
    { test: /(^|\.)(youtube\.com|youtube-nocookie\.com|youtu\.be)$/i, name: 'YouTube video' },
    { test: /(^|\.)bandcamp\.com$/i, name: 'Bandcamp player' },
    { test: /(^|\.)giphy\.com$/i, name: 'GIPHY animation' }
  ];

  /* An app's own iframe is the app's finding, not a theme one. shopify-block
     is the wrapper Shopify gives every app embed block, so it catches apps
     this list has never heard of as well as the ones it has. */
  var APP_IFRAME_ANCESTORS = '.shopify-block,[id^="shopify-block"],[class*="jdgm"],[class*="judgeme"],' +
    '[class*="postscript"],[class*="klaviyo"],[class*="adoric"],[class*="offerly"],' +
    '[class*="Offerly"],[class*="restock"],[class*="shopify-chat"],[id*="shopify-chat"]';

  function embedFrameName(frame) {
    var src = frame.getAttribute('src') || frame.getAttribute('data-src') || '';
    if (src) {
      try {
        var host = new URL(src, window.location.href).hostname;
        for (var i = 0; i < EMBED_HOST_NAMES.length; i++) {
          if (EMBED_HOST_NAMES[i].test.test(host)) return EMBED_HOST_NAMES[i].name;
        }
      } catch (e) { /* an unparseable src just falls through to the generic name */ }
    }
    return 'Embedded media';
  }

  function nameArticleEmbedFrames(root) {
    var body = document.body;
    if (!body || !body.classList.contains('template-article')) return;
    var main = document.getElementById('main');
    if (!main) return;

    var frames = main.querySelectorAll('iframe');
    if (!frames.length) return;

    /* Two passes. The first records the names already on the page, including
       ones an earlier pass of this function wrote, so a re-run assigns the
       same strings and a repeat never collides with a title the merchant or
       an embed provider set themselves. */
    var used = Object.create(null);
    var pending = [];
    for (var i = 0; i < frames.length; i++) {
      var frame = frames[i];
      if (frame.closest && frame.closest(APP_IFRAME_ANCESTORS)) continue;
      var title = (frame.getAttribute('title') || '').trim();
      if (title) { used[title.toLowerCase()] = true; continue; }
      /* An aria-label already names the frame; overwriting it with a host
         guess would be a downgrade. */
      if ((frame.getAttribute('aria-label') || '').trim()) continue;
      pending.push(frame);
    }

    for (var j = 0; j < pending.length; j++) {
      var base = embedFrameName(pending[j]);
      var label = base;
      var n = 1;
      while (used[label.toLowerCase()]) {
        n++;
        label = base + ' ' + n;
      }
      used[label.toLowerCase()] = true;
      pending[j].setAttribute('title', label);
    }
  }

  /* layout/theme.liquid rewrites every .rte table into a .table-wrapper with
     overflow-x:auto. Only give it a tab stop and a name while it is really
     overflowing: a table that fits would otherwise become a focus stop that
     announces a region and does nothing. Re-checked on resize, and the flag
     means we only ever remove attributes we put there. */
  function focusableScrollableTables(root) {
    var wrappers = document.querySelectorAll('.table-wrapper');
    for (var i = 0; i < wrappers.length; i++) {
      var wrap = wrappers[i];
      var marked = wrap.getAttribute('data-tl-scroll-region') === 'true';
      var scrolls = wrap.scrollWidth - wrap.clientWidth > 1;

      if (scrolls && !marked) {
        if (!wrap.hasAttribute('tabindex')) wrap.setAttribute('tabindex', '0');
        if (!wrap.getAttribute('role')) wrap.setAttribute('role', 'region');
        if (!wrap.getAttribute('aria-label') && !wrap.getAttribute('aria-labelledby')) {
          /* A caption is the table's own name and is written by whoever wrote
             the article, so point at it rather than copying it. */
          var caption = wrap.querySelector('table > caption');
          if (caption && caption.textContent.trim()) {
            if (!caption.id) caption.id = 'tl-table-caption-' + (++labelSeq);
            wrap.setAttribute('aria-labelledby', caption.id);
          } else {
            wrap.setAttribute('aria-label', 'Table, scroll horizontally');
          }
        }
        wrap.setAttribute('data-tl-scroll-region', 'true');
      } else if (!scrolls && marked) {
        wrap.removeAttribute('tabindex');
        if (wrap.getAttribute('role') === 'region') wrap.removeAttribute('role');
        if (wrap.getAttribute('aria-label') === 'Table, scroll horizontally') wrap.removeAttribute('aria-label');
        wrap.removeAttribute('aria-labelledby');
        wrap.removeAttribute('data-tl-scroll-region');
      }
    }
  }

  /* blocks/_grid-media, _grid-text, _slide-image, _slide-video (moved off
     native files for the Combine 4.2 upgrade): the overlay anchor makes the
     whole card clickable, and these cards usually have no inner CTA, so it
     is the only path to the destination. Stock ships it aria-hidden, which
     hides the only link from assistive tech and fails aria-hidden-focus. It
     becomes a real named link: named from the card's image alt, falling back
     to a generic label so it is never unnamed (axe link-name). Only these
     four blocks use card__whole-link--alternate, so nothing else is touched. */
  function nameWholeCardLinks(root) {
    var links = root.querySelectorAll('a.card__whole-link.card__whole-link--alternate[aria-hidden="true"]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var card = link.closest('[id^="element-"]') || link.parentNode;
      var img = card ? card.querySelector('img[alt]:not([alt=""])') : null;
      var label = img ? (img.getAttribute('alt') || '').trim() : '';
      link.removeAttribute('aria-hidden');
      if (!link.getAttribute('aria-label')) link.setAttribute('aria-label', label || 'View this section');
    }
  }

  /* sections/product-quick-view.liquid (moved off native for the 4.2
     upgrade): the close button ships aria-hidden, which removes the only way
     out of the dialog from assistive tech. Unhide it and name it. */
  function nameQuickViewClose(root) {
    var buttons = root.querySelectorAll('.product-quick-view__close[aria-hidden="true"]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].removeAttribute('aria-hidden');
      if (!buttons[i].getAttribute('aria-label')) buttons[i].setAttribute('aria-label', 'Close quick view');
    }
  }

  /* snippets/video-component.liquid (moved off native for the 4.2 upgrade):
     the play link has no text when the video has no title and no caption
     content, so it is an unnamed link. Same condition the Liquid used: no
     title, no aria-label, no text of its own. */
  function namePlayVideoLinks(root) {
    var links = root.querySelectorAll('a.video-component__link:not([title]):not([aria-label])');
    for (var i = 0; i < links.length; i++) {
      if ((links[i].textContent || '').trim() === '') links[i].setAttribute('aria-label', 'Play video');
    }
  }

  /* snippets/cart-form.liquid and cart-form-page.liquid: each line item's
     thumbnail is a link to the product whose only content is the image, and
     that image carries no alt, so the link has no accessible name (axe
     link-name, found on the cart drawer and the filled cart page). Name it
     from the product title the line item already exposes in data-title.
     Kept as a link: tapping the thumbnail to reach the product must keep
     working for everyone. */
  function nameCartThumbnailLinks(root) {
    var links = root.querySelectorAll('a.cart-item__thumbnail:not([aria-label])');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if ((link.textContent || '').trim() !== '' || link.querySelector('img[alt]:not([alt=""])')) continue;
      var item = link.closest('[data-js-cart-item]');
      var title = item ? (item.getAttribute('data-title') || '').trim() : '';
      if (title) link.setAttribute('aria-label', title);
    }
  }

  function run() {
    try {
      unhideOpenOfferlyModal(document);
      nameOfferlyInputs(document);
      syncEmptyHeadings(document);
      nameRangeSliderThumbs(document);
      nameSidebarDrawers(document);
      dropJudgemeGridRoles(document);
      patchAdoricBundle(document);
      patchGalleryThumbnails(document);
      dropDanglingSortDescribedby(document);
      nameCartDiscountInput(document);
      fixArticleNavHeadingLevel(document);
      nameHeaderNavLandmarks(document);
      nameFooterMenuLandmarks(document);
      adoptStickyHeaderClone(document);
      focusableSliderHolders(document);
      hideDecorativeVideos(document);
      nameStickyAddToCart(document);
      syncAriaDisabledButtons(document);
      patchZoomOverlay(document);
      markOverlayDialogs(document);
      nameArticleEmbedFrames(document);
      focusableScrollableTables(document);
      nameWholeCardLinks(document);
      nameQuickViewClose(document);
      namePlayVideoLinks(document);
      nameCartThumbnailLinks(document);
    } catch (e) { /* never break the page for an a11y patch */ }
  }

  /* Offerly is not on every page. Watching the whole body everywhere costs
     two document-wide queries per animation frame on pages that will never
     have anything to patch, so bail out once nothing Offerly-shaped is
     present and no new candidate has appeared. */
  function hasOfferly() {
    return !!document.querySelector('.offerInput, [class*="offerly"], #mobileModalProductTitle, #modalProductTitle');
  }

  /* The bail-out this used to carry is gone. The THEME_PATCHES above apply on
     every page, and several of their targets (the sticky header clone, the
     zoom overlay, slider holders, the sticky add to cart bar) only exist once
     script has run, so gating the observer on Offerly or the price slider
     would mean never seeing them. The rAF throttle below is what keeps the
     cost bounded instead. */
  function hasPatchTarget() {
    return true;
  }

  /* The bundle elements are defined by Adoric's app extension, which loads
     independently of this file, and a shadow root can be attached with no
     light-DOM mutation for the page observer below to see. Wait for the
     definition instead, the same way assets/ml-bundle-adoric.js does. */
  function bindAdoricHosts() {
    if (!window.customElements) return;
    for (var i = 0; i < ADORIC_HOSTS.length; i++) {
      (function (tag) {
        window.customElements.whenDefined(tag).then(function () {
          /* whenDefined resolves as the element upgrades, so yield once
             before reading its shadow root. */
          setTimeout(function () {
            try { patchAdoricBundle(document); } catch (e) { /* never break the widget */ }
          }, 0);
        });
      })(ADORIC_HOSTS[i]);
    }
  }

  function start() {
    bindZoomTriggers();
    bindAdoricHosts();
    run();
    /* Tells the CSS that the exact, text-driven marking above is now in
       charge, which retires the coarser :has() fallback. Set after the first
       run so there is no frame in which neither layer is applied. */
    document.documentElement.classList.add('tl-a11y-ready');

    /* Whether a table overflows is a layout fact, and layout changes without
       any DOM mutation for the observer below to see: a viewport rotation, a
       late web font, an image settling. Re-check on both. */
    var tableCheckScheduled = false;
    function recheckTables() {
      if (tableCheckScheduled) return;
      tableCheckScheduled = true;
      window.requestAnimationFrame(function () {
        tableCheckScheduled = false;
        try { focusableScrollableTables(document); } catch (e) { /* never break the page */ }
      });
    }
    window.addEventListener('resize', recheckTables);
    window.addEventListener('load', recheckTables);

    if (!('MutationObserver' in window)) return;
    /* At most one pass per MIN_GAP ms. The first mutation after a quiet
       spell still runs on the next frame; only a continuous stream (card
       sliders and lazy images while a collection page loads) is thinned,
       from every frame to a few times a second. */
    var MIN_GAP = 250;
    var scheduled = false;
    var lastRun = 0;
    var observer = new MutationObserver(function (records) {
      if (!hasPatchTarget()) return;
      /* Offerly injects its whole modal in one go. Waiting a frame to patch it
         leaves the empty headings unmarked for that frame, which is exactly
         what a scanner snapshots, so handle an insertion synchronously. */
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.matches('.OfferlyModal') || node.querySelector('.OfferlyModal')) {
            run();
            return;
          }
        }
      }
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(function () {
        window.requestAnimationFrame(function () {
          scheduled = false;
          lastRun = Date.now();
          run();
        });
      }, Math.max(0, lastRun + MIN_GAP - Date.now()));
    });
    /* Attributes matter as much as children here: Postscript closes by flipping
       data-ps-popup-status, and a reused Offerly modal reopens through a style
       change. Watching children alone would leave a visible dialog marked
       aria-hidden until some unrelated mutation happened to fire. The filter
       keeps this off the hot path, and every write below is guarded, so the
       one extra pass our own change triggers settles immediately. */
    /* documentElement rather than body: watching the root also catches a
       modal appended outside body's own wrappers, and costs nothing extra
       given the filters below. */
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-hidden', 'data-ps-popup-status', 'data-original-aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
